import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import { Person } from "models/Person";
import { Project, ProjectContract, ProjectHours } from "models/Project";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { bindActionCreators } from "redux";
import {
    getProjectContracts,
    getProjectHours,
    getWorkerProjects
} from "services/AccountApi";
import {
    accountPushEnabledLocally,
    enableAccountWebPush,
    webPushSupported
} from "services/WebPush";
import { RootStateAccount } from "storeAccount/accountStore";
import { getInvoices } from "storeAccount/actions/Billing";
import { InvoiceState } from "storeAccount/reducers/Invoices";
import { useAccountTranslation } from "./translations";

interface Props {
    customer: Account;
    person: Person;
    invoiceState: InvoiceState;
}

interface Actions {
    getInvoices: () => void;
}

interface ProjectSnapshot {
    project: Project;
    hours: ProjectHours;
    contracts: ProjectContract[];
}

export const AccountHome: React.FC<Props & Actions> = ({ customer, person, invoiceState, getInvoices }) => {
    const { t, intlLocale } = useAccountTranslation();
    const [projects, setProjects] = useState<ProjectSnapshot[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [projectError, setProjectError] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(() => accountPushEnabledLocally(customer.id));
    const [pushBusy, setPushBusy] = useState(false);
    const [pushDismissed, setPushDismissed] = useState(false);
    const [pushError, setPushError] = useState(false);
    const loadSequence = useRef(0);

    useEffect(() => {
        getInvoices();
    }, [getInvoices]);

    useEffect(() => {
        const sequence = ++loadSequence.current;
        setLoadingProjects(true);
        setProjectError(false);
        setPushEnabled(accountPushEnabledLocally(customer.id));
        setPushDismissed(false);
        setPushError(false);

        getWorkerProjects(customer.id)
            .then(values => Promise.all(values.map(async project => {
                const hours = await getProjectHours(project.id, customer.id);
                const contracts = hours.worker_account_id || hours.can_approve
                    ? await getProjectContracts(project.id, customer.id)
                    : [];
                return { project, hours, contracts };
            })))
            .then(values => {
                if (sequence === loadSequence.current) {
                    setProjects(values);
                }
            })
            .catch(() => {
                if (sequence === loadSequence.current) {
                    setProjectError(true);
                }
            })
            .finally(() => {
                if (sequence === loadSequence.current) {
                    setLoadingProjects(false);
                }
            });
    }, [customer.id]);

    const invoices = invoiceState.getInvoicesByCustomerId(customer.id);
    const openInvoices = invoices.filter(invoice => !invoice.quote && !invoice.draft && !invoice.IsPaid());
    const openQuotes = invoices.filter(invoice => invoice.quote && !invoice.draft);
    const outstandingAmount = openInvoices.reduce((total, invoice) => total + invoice.CalcPendingTotal(), 0);
    const pendingEntries = projects.flatMap(({ hours }) =>
        hours.workers.flatMap(worker => worker.entries.filter(entry => entry.status === "pending_approval"))
    );
    const approvablePendingEntries = projects
        .filter(({ hours }) => hours.can_approve)
        .flatMap(({ hours }) =>
            hours.workers.flatMap(worker => worker.entries.filter(entry => entry.status === "pending_approval"))
        );
    const pendingMinutes = pendingEntries.reduce((total, entry) => total + entry.minutes, 0);
    const unsignedContracts = projects.reduce(
        (total, project) => total + project.contracts.filter(contract => contract.signature_status === "unsigned").length,
        0
    );
    const blockingAttachments = Array.from(new Map(
        projects.flatMap(({ hours }) => (hours.attachment_requirements ?? [])
            .filter(value => value.requirement.required_for_hours && value.status !== "approved"))
            .map(value => [value.requirement.id, value])
    ).values());
    const expiredAttachments = blockingAttachments.filter(value => value.status === "expired");
    const recentInvoices = [...openInvoices]
        .sort((first, second) => second.GetCreatedDate().getTime() - first.GetCreatedDate().getTime())
        .slice(0, 3);
    const money = new Intl.NumberFormat(intlLocale, { style: "currency", currency: "EUR" });
    const pendingDuration = [
        pendingMinutes >= 60 ? `${Math.floor(pendingMinutes / 60)}${t("hourShort")}` : "",
        pendingMinutes % 60 ? `${pendingMinutes % 60}${t("minuteShort")}` : ""
    ].filter(Boolean).join(" ") || `0${t("minuteShort")}`;
    const shouldOfferPush = webPushSupported()
        && Notification.permission !== "denied"
        && !pushEnabled
        && !pushDismissed;
    const ibanMissing = !(customer.bank_accounts ?? []).some(bankAccount => !!bankAccount.iban?.trim());
    const vatMissing = !customer.vat_number?.trim();
    const attentionCount = openInvoices.length
        + unsignedContracts
        + approvablePendingEntries.length
        + blockingAttachments.length
        + (ibanMissing ? 1 : 0)
        + (vatMissing ? 1 : 0);

    const activatePush = async () => {
        setPushBusy(true);
        setPushError(false);
        try {
            await enableAccountWebPush(customer.id);
            setPushEnabled(true);
        } catch {
            setPushError(true);
        } finally {
            setPushBusy(false);
        }
    };

    const statCards = [
        {
            label: t("openInvoices"),
            value: openInvoices.length,
            detail: money.format(outstandingAmount),
            icon: "mdi-file-document-outline",
            tone: "coral",
            path: "/invoices"
        },
        {
            label: t("openQuotes"),
            value: openQuotes.length,
            detail: openQuotes.length ? t("attentionNeeded") : t("allUpToDate"),
            icon: "mdi-file-sign",
            tone: "violet",
            path: "/quotes"
        },
        {
            label: t("activeProjects"),
            value: projects.length,
            detail: unsignedContracts ? t("contractsWaiting", { count: unsignedContracts }) : t("allUpToDate"),
            icon: "mdi-briefcase-outline",
            tone: "blue",
            path: "/projects"
        },
        {
            label: t("pendingHours"),
            value: pendingEntries.length,
            detail: pendingEntries.length ? pendingDuration : t("allUpToDate"),
            icon: "mdi-clock-check-outline",
            tone: "green",
            path: "/projects"
        }
    ];

    return <div className="portal-home">
        <section className="portal-home-hero">
            <div>
                <span className="portal-eyebrow">{customer.legal_name}</span>
                <h1>{t("welcomeBack", { name: person.name })}</h1>
                <p>{t("portalOverviewIntro")}</p>
            </div>
            <div className="portal-hero-mark" aria-hidden="true">
                <i className="mdi mdi-view-dashboard-variant-outline" />
            </div>
        </section>

        {shouldOfferPush && <section className="portal-push-banner">
            <div className="portal-push-banner-icon"><i className="mdi mdi-bell-ring-outline" /></div>
            <div className="portal-push-banner-copy">
                <strong>{t("pushBannerTitle")}</strong>
                <span>{t("pushBannerDescription")}</span>
                {pushError && <small>{t("pushUpdateError")}</small>}
            </div>
            <div className="portal-push-banner-actions">
                <button type="button" className="portal-button portal-button-light" onClick={() => setPushDismissed(true)}>
                    {t("later")}
                </button>
                <button type="button" className="portal-button portal-button-white" disabled={pushBusy} onClick={activatePush}>
                    {pushBusy ? "…" : t("pushEnable")}
                </button>
            </div>
        </section>}

        <div className="portal-stat-grid" aria-label={t("overview")}>
            {statCards.map(card => <Link key={card.label} to={card.path} className="portal-stat-card">
                <span className={`portal-stat-icon portal-stat-icon--${card.tone}`}>
                    <i className={`mdi ${card.icon}`} aria-hidden="true" />
                </span>
                <span className="portal-stat-copy">
                    <span className="portal-stat-label">{card.label}</span>
                    <strong>{loadingProjects && card.path === "/projects" ? "…" : card.value}</strong>
                    <small>{card.detail}</small>
                </span>
                <i className="mdi mdi-chevron-right portal-stat-arrow" aria-hidden="true" />
            </Link>)}
        </div>

        {projectError && <div className="portal-inline-alert">
            <i className="mdi mdi-alert-circle-outline" />
            <span>{t("dashboardLoadError")}</span>
        </div>}

        <div className="portal-home-columns">
            <section className="portal-panel portal-attention-panel">
                <div className="portal-panel-heading">
                    <div>
                        <span className="portal-eyebrow">{t("attentionNeeded")}</span>
                        <h2>{attentionCount ? t("actionRequired") : t("allUpToDate")}</h2>
                    </div>
                    {attentionCount > 0 && <span className="portal-count-pill">{attentionCount}</span>}
                </div>

                {attentionCount === 0 && <div className="portal-empty-state">
                    <span><i className="mdi mdi-check" /></span>
                    <div>
                        <strong>{t("allUpToDate")}</strong>
                        <p>{t("allUpToDateDescription")}</p>
                    </div>
                </div>}

                <div className="portal-action-list">
                    {ibanMissing && <Link to="/my-details" className="portal-action-row">
                        <span className="portal-action-dot portal-action-dot--coral"><i className="mdi mdi-bank-off-outline" /></span>
                        <span><strong>{t("iban")}</strong><small>{t("ibanMissing")}</small></span>
                        <i className="mdi mdi-arrow-right" />
                    </Link>}
                    {vatMissing && <Link to="/my-details" className="portal-action-row">
                        <span className="portal-action-dot portal-action-dot--coral"><i className="mdi mdi-receipt" /></span>
                        <span><strong>{t("vatNumber")}</strong><small>{t("vatMissing")}</small></span>
                        <i className="mdi mdi-arrow-right" />
                    </Link>}
                    {openInvoices.length > 0 && <Link to="/invoices" className="portal-action-row">
                        <span className="portal-action-dot portal-action-dot--coral"><i className="mdi mdi-credit-card-outline" /></span>
                        <span><strong>{t("openInvoices")}</strong><small>{t("invoiceAttention", { count: openInvoices.length })}</small></span>
                        <i className="mdi mdi-arrow-right" />
                    </Link>}
                    {unsignedContracts > 0 && <Link to="/projects" className="portal-action-row">
                        <span className="portal-action-dot portal-action-dot--violet"><i className="mdi mdi-draw-pen" /></span>
                        <span><strong>{t("contracts")}</strong><small>{t("contractAttention", { count: unsignedContracts })}</small></span>
                        <i className="mdi mdi-arrow-right" />
                    </Link>}
                    {approvablePendingEntries.length > 0 && <Link to="/projects" className="portal-action-row">
                        <span className="portal-action-dot portal-action-dot--green"><i className="mdi mdi-clock-outline" /></span>
                        <span><strong>{t("pendingHours")}</strong><small>{t("hoursAttention", { count: approvablePendingEntries.length })}</small></span>
                        <i className="mdi mdi-arrow-right" />
                    </Link>}
                    {blockingAttachments.length > 0 && <Link to="/my-details" className="portal-action-row">
                        <span className="portal-action-dot portal-action-dot--coral"><i className="mdi mdi-file-alert-outline" /></span>
                        <span>
                            <strong>{t("documents")}</strong>
                            <small>{expiredAttachments.length
                                ? t("documentsExpiredAttention", { count: expiredAttachments.length })
                                : t("documentsAttention", { count: blockingAttachments.length })}</small>
                        </span>
                        <i className="mdi mdi-arrow-right" />
                    </Link>}
                </div>
            </section>

            <section className="portal-panel">
                <div className="portal-panel-heading">
                    <div>
                        <span className="portal-eyebrow">{t("financialOverview")}</span>
                        <h2>{t("recentInvoices")}</h2>
                    </div>
                    <Link to="/invoices">{t("viewAll")}</Link>
                </div>
                {recentInvoices.length === 0
                    ? <div className="portal-compact-empty"><i className="mdi mdi-check-circle-outline" />{t("noOpenInvoices")}</div>
                    : <div className="portal-recent-list">
                        {recentInvoices.map((invoice: Invoice) => <Link to="/invoices" key={invoice.id}>
                            <span><strong>{invoice.GetDisplayName() || t("noName")}</strong><small>{new Intl.DateTimeFormat(intlLocale).format(invoice.GetCreatedDate())}</small></span>
                            <b>{money.format(invoice.CalcPendingTotal())}</b>
                        </Link>)}
                    </div>}
            </section>
        </div>

        <section className="portal-panel portal-project-strip">
            <div className="portal-panel-heading">
                <div>
                    <span className="portal-eyebrow">{t("activeProjects")}</span>
                    <h2>{t("myProjects")}</h2>
                </div>
                <Link to="/projects">{t("viewAll")}</Link>
            </div>
            {loadingProjects
                ? <div className="portal-loading-row">{t("loadingOverview")}</div>
                : projects.length === 0
                    ? <div className="portal-compact-empty"><i className="mdi mdi-briefcase-check-outline" />{t("noProjectsDescription")}</div>
                    : <div className="portal-project-cards">
                        {projects.slice(0, 3).map(({ project, hours: projectHours }) => {
                            const projectPending = projectHours.workers.reduce(
                                (total, worker) => total + worker.entries.filter(entry => entry.status === "pending_approval").length,
                                0
                            );
                            return <Link to="/projects" key={project.id}>
                                <span className="portal-project-icon"><i className="mdi mdi-briefcase-outline" /></span>
                                <span>
                                    <strong>{project.name}</strong>
                                    <small>{projectPending ? t("hoursAttention", { count: projectPending }) : t("allUpToDate")}</small>
                                </span>
                                <i className="mdi mdi-chevron-right" />
                            </Link>;
                        })}
                    </div>}
        </section>
    </div>;
};

const mapStateToProps = (state: RootStateAccount) => ({
    invoiceState: state.Invoices
});

const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ getInvoices }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AccountHome);
